import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import './CustomFormFields.scss';
import ObjectIcon from '../shared/ObjectIcon';
import { Combobox, comboboxFilterAndLimit, IconSettings } from '@salesforce/design-system-react';
import { assigneeSearch } from '../../../api/emailsApi/task';

const debounce = (cb, delay) => () => {
  const timeout = setTimeout(cb, delay);
  return () => clearTimeout(timeout);
};

const assigneeLookupItem = (assignee) => {
  return (
    <div className='lookup-menu-option'>
      {assignee.option?.icon}
      <span className="slds-listbox__option-text slds-listbox__option-text_entity lookup-menu-option-label">
        {assignee.option?.label || ''}
      </span>
    </div>
  );
};
assigneeLookupItem.displayName = 'CustomMenuItem';


export const AssigneeLookupField = ({ required, label, fieldName, handleChange, error }) => {
  // Use current logged in salesforce user as default
  const salesforceUserId = useSelector((state) => state.auth.user?.salesforceUserId);
  const salesforceDisplayName = useSelector((state) => state.auth.user?.salesforceDisplayName);

  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState({
    selection: [
      {
        label: salesforceDisplayName,
        value: salesforceUserId,
        icon: <ObjectIcon objectApiName='User' className='lookup-option'/>,
      },
    ],
  });

  const updateAssigneeOptions = async () => {
    setIsLoading(true);
    const assigneeResponse = await assigneeSearch('User', searchQuery);

    // Remove assignees who are not active in Salesforce
    const filteredAssigneeResponse = {
      ...assigneeResponse,
      data: {
        ...assigneeResponse.data,
        ContactRecords: assigneeResponse.data.ContactRecords.filter((record) => record.displayFields.IsActive),
      },
    };

    // Sort assignees by first and last name
    const sortedAssigneeRecords = (filteredAssigneeResponse.data?.ContactRecords || []).sort((a, b) => (a.displayFields?.Name || '').localeCompare(b.displayFields?.Name || ''));

    const options = sortedAssigneeRecords.map((assignee) => ({
      label: assignee.displayFields?.Name,
      value: assignee.displayFields?.Id,
      icon: <ObjectIcon objectApiName='User' className='lookup-option'/>,
    }));
    setIsLoading(false);
    setAssigneeOptions(options);
  };

  // Setting current SF user has default value in form
  useEffect(() => {
    handleChange(null, { fieldName, value: salesforceUserId });
  }, []);

  const debounceEffect = () => {
    if (![0, 1, 2].includes(searchQuery.length)) {
      updateAssigneeOptions();
    }
  };

  useEffect(debounce(() => {
    debounceEffect();
  }, 700), [searchQuery]);

  return (
    <IconSettings iconPath="/assets">
      <Combobox
        classNameContainer={error === '' ? '' : 'lookup-error'}
        id="combobox-inline-single"
        required={required}
        name={fieldName}
        errorText={error}
        defaultValue={selectedAssignee.selection}
        hasMenuSpinner={isLoading}
        events={{
          onChange: (event, { value }) => {
            setSearchQuery(value);
          },
          onRequestRemoveSelectedOption: (event, data) => {
            setSelectedAssignee({
              selection: data.selection,
            });
            handleChange(event, { fieldName, value: null });
          },
          onSelect: (event, data) => {
            setSelectedAssignee({
              selection: data.selection,
            });
            handleChange(event, { fieldName, value: data.selection[0].value });
          },
        }}
        labels={{
          label,
          placeholder: `Search ${label}`,
          noOptionsFound: isLoading ? '' : 'No users found',
        }}
        onRenderMenuItem={(assigneeOptions) => assigneeLookupItem(assigneeOptions)}
        options={comboboxFilterAndLimit({
          inputValue: searchQuery,
          options: assigneeOptions,
          selection: selectedAssignee.selection,
          limit: 10,
        })}
        menuItemVisibleLength={5}
        selection={selectedAssignee.selection}
        value={
          selectedAssignee.selection.label
        }
        variant='inline-listbox'
      />
    </IconSettings>
  );
};

AssigneeLookupField.propTypes = {
  required: PropTypes.bool,
  label: PropTypes.string,
  fieldName: PropTypes.string,
  handleChange: PropTypes.func,
  error: PropTypes.string,
};
